/**
 * Phase 4 — Shift
 *
 * Three-turn structure:
 * Turn 0: [Begin phase] → Claude generates a personalized reframe question from the pattern
 * Turn 1: User responds → Claude reflects + deepens
 *         → After Turn 1: card selection (Soul Perspective or Reframe Invitation)
 * Turn 2: User responds to deepening (optional) → Claude final closing reflection
 */
import { useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useScreenTrail } from '../../lib/sessionBack'
import Retained from '../ui/Retained'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'
import type { SessionMessage, SessionMode } from '../../types/session'
import { callClaude } from '../../lib/claude'
import { errorCodeOf, guideErrorKey } from '../../lib/guideErrors'
import { parseOptions } from '../../lib/parseOptions'
import InsightCard from './InsightCard'
import type { InsightCardType } from './InsightCard'
import VoiceButton from '../ui/VoiceButton'
import LoadingSpiral from '../ui/LoadingSpiral'
import IntuitiveChoice from '../ui/IntuitiveChoice'
import type { ChoiceState } from '../ui/IntuitiveChoice'

interface ShiftProps {
  sessionId: string
  userId?: string
  emotion: string
  intensity: number
  storyText: string
  patternReflection: string
  /**
   * The person's own words for the age of the feeling, from the optional field
   * on the Feel reflection; '' when they gave none. Sent to the guide as user
   * data (see lib/age.ts for what counts as an answer).
   */
  ageText?: string
  firstName?: string
  lifePathNumber?: number
  seriesContext?: string
  /**
   * Awakening-only: short summary of recurring threads from past sessions.
   * Forwarded to callClaude → injected into phase 4 system prompt as
   * background awareness for the guide.
   */
  clusterContext?: string
  /**
   * Mind-level lens — when 'the_work', the standard 'done' state advances to
   * 'turnaround-input' instead of immediately calling onComplete. The
   * turnaround is the final step of Byron Katie's The Work: finding the
   * opposite of the original belief.
   */
  sessionMode?: SessionMode
  onComplete: (reframeResponse: string) => void
}

type UIState =
  | 'intuitive-shift'      // what if it were different — intuitive choice pre-step
  | 'generating'           // fetching personalized reframe question from Claude
  | 'awaiting'             // first input — user responds to reframe question
  | 'reflecting'           // waiting for Claude turn 1
  | 'card-selection'       // choose Soul Perspective or Reframe Invitation
  | 'showing-card'         // InsightCard overlay
  | 'deepening-input'      // Claude replied, showing second input (optional)
  | 'deepening-reflecting' // waiting for Claude turn 2
  | 'done'                 // final Claude reply shown
  // ── The Work turnaround states (only when sessionMode === 'the_work') ──
  | 'turnaround-input'     // Claude generates turnaround variants; user reflects
  | 'error'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey | null> = {
  'intuitive-shift':      'shift.feeling',
  'generating':           'shift.reframe',
  'awaiting':             'shift.reframe',
  'reflecting':           'shift.reframe',
  'card-selection':       'shift.cardChoice',
  // The insight card is on screen in Shift's place and reports its own number.
  'showing-card':         null,
  'deepening-input':      'shift.deepening',
  'deepening-reflecting': 'shift.deepening',
  'done':                 'shift.reflection',
  'turnaround-input':     'shift.work.turnaround',
  'error':                'shift.error',
}

export default function Shift({ sessionId, userId = '', emotion, intensity, storyText, patternReflection, ageText, firstName = '', lifePathNumber, seriesContext, clusterContext, sessionMode, onComplete }: ShiftProps) {
  const { t } = useTranslation()

  /*
   * Starter answers offered when the user says they don't know how to respond.
   * Read from the locale, with an English fallback at the exact expected
   * length so a bad locale value cannot render an empty prompt list at the
   * moment someone has said they are stuck.
   */
  const guidedPrompts = (() => {
    const fallback = [
      "I honestly don't know yet.",
      "Maybe it's protecting me from feeling out of control.",
      'I think it keeps me from being hurt again.',
      "I'm not sure, but I'm willing to look at it.",
    ]
    const v = t('shift.guidedPrompts', { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  })()

  const [uiState, setUiState] = useState<UIState>('intuitive-shift')
  useScreenId(SCREEN_OF[uiState])
  const [shiftFeeling, setShiftFeeling] = useState('')
  const [shiftOptions, setShiftOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [reframeQuestion, setReframeQuestion] = useState<string | null>(null)
  const [userResponse, setUserResponse] = useState('')
  const [claudeReply, setClaudeReply] = useState<string | null>(null)
  const [deepeningResponse, setDeepeningResponse] = useState('')
  const [finalReply, setFinalReply] = useState<string | null>(null)
  // Why the last call failed, as a code the error screen translates (lib/claude.ts).
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // Where "Try again" goes. The main path restarts from the reframe question
  // ('generating'); a failed Work turnaround retries the turnaround instead.
  const [errorRetryTo, setErrorRetryTo] = useState<UIState>('generating')
  const [showGuided, setShowGuided] = useState(false)
  // The insight card the person opened, or null if none is open. Once opened it
  // stays alive (hidden) when they move on to the next screen, so Back from there
  // returns to the card's last screen as it was left, not to a rebuilt one.
  // Choosing a different card, or changing an answer the card was written from,
  // replaces it.
  // What the person chose on 4.1. Kept here, not in the picker, so it
  // outlives the picker: Back to this screen shows the same chips and words, and
  // Continue with nothing touched hands on the same answer (#68).
  const [feelingPick, setFeelingPick] = useState<ChoiceState | undefined>()

  const [cardOpened, setCardOpened] = useState<InsightCardType | null>(null)

  // ─── The Work turnaround state (only when sessionMode === 'the_work') ───
  // turnaroundReflection — Claude's response to [TW-TURNAROUND] (lists 2-3 turnarounds)
  // turnaroundResponse — user's answer to "Which feels most alive?"
  // turnaroundLoading — true while fetching the turnaround reflection
  const [turnaroundReflection, setTurnaroundReflection] = useState<string | null>(null)
  const [turnaroundResponse, setTurnaroundResponse] = useState('')
  const [turnaroundLoading, setTurnaroundLoading] = useState(false)

  // sessionMode threaded to Claude so the Edge Function appends the
  // turnaround block to the case 4 system prompt.
  // clusterContext: Awakening-only background awareness — injected by the
  // Edge Function into the phase 4 system prompt; never referenced directly.
  const opts = { phase: 4, emotion, intensity, firstName, sessionId, lifePathNumber, seriesContext, clusterContext, sessionMode, ageText: ageText || undefined }

  // What each reply was written for, so Back and then Continue on the same words
  // shows the reply already there, and different words ask for a new one.
  const replyFor = useRef<string | null>(null)
  const finalFor = useRef<string | null>(null)

  // ─── Back ──────────────────────────────────────────────────────────────────
  // The screens Shift has shown, in order (lib/sessionBack.ts). 'showing-card'
  // is one of them: the card hosted there has its own screens and is asked
  // first, and Back leaves it only from its first screen.
  useScreenTrail<UIState>({
    screen: uiState,
    go: setUiState,
    kind: s =>
      s === 'generating' || s === 'reflecting' || s === 'deepening-reflecting' ? 'loading'
        // The turnaround screen is a wait until the guide's variants are on it, and
        // a wait is never a place to return to (it would only ask again).
        : s === 'turnaround-input' && (turnaroundLoading || !turnaroundReflection) ? 'loading'
        : s === 'error' ? 'error'
        : 'rest',
  })

  // Shown when the model's feeling options cannot be used. Read from the
  // catalogue so the fallback is in the session's language too.
  const shiftFallback = t('shift.shiftOptions', { returnObjects: true }) as string[]

  useEffect(() => {
    callClaude({
      phase: 20, emotion, intensity,
      messages: [{ role: 'user', content: '[Generate options]' }],
      firstName, sessionId,
      optionContext: `Question: "What would it feel like if even a small part of this situation could be different?" The pattern named: "${patternReflection}". Generate 9 feeling options for the specific relief this person might imagine — what would shift in their body, relationships, or inner world if this pattern loosened. VARY the structure: some are visceral body sensations ("A loosening in my chest"), some are relational ("More room to just exist without proving anything"), some are internal states. Every option must be specific to this pattern — if it sounds like a generic wellness feeling, rewrite it. No two options should be variations of the same feeling.`,
    }).then(result => {
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        if (parsed.length >= 7) setShiftOptions(parsed)
        else setShiftOptions(shiftFallback)
      } else {
        setShiftOptions(shiftFallback)
      }
      setOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── The Work — fire turnaround fetch when entering turnaround-input ────
  // Side-effect rather than synchronous call so render doesn't suspend.
  useEffect(() => {
    if (uiState !== 'turnaround-input' || turnaroundReflection !== null || turnaroundLoading) return
    void fetchTurnaround()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState])

  // Turn 0: generate a personalized reframe question based on the pattern
  useEffect(() => {
    if (uiState !== 'generating') return
    const feelingContext = shiftFeeling ? ` If different, they imagine: ${shiftFeeling}.` : ''
    const contextMessages: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: `[Pattern named: "${patternReflection}"]${feelingContext}` },
      { role: 'user', content: '[Begin phase]' },
    ]
    callClaude({ ...opts, messages: contextMessages }).then(result => {
      if ('error' in result) {
        setErrorCode(errorCodeOf(result))
        setUiState('error')
        return
      }
      const cleaned = result.response.replace(/\[Begin phase\]/gi, '').trim()
      setReframeQuestion(cleaned)
      setUiState('awaiting')
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState])

  function buildBaseMessages(): SessionMessage[] {
    return [
      { role: 'user',      content: storyText },
      { role: 'assistant', content: `The pattern we named: "${patternReflection}".` },
      { role: 'user',      content: '[Begin phase]' },
      { role: 'assistant', content: reframeQuestion ?? '' },
    ]
  }

  // Turn 1: user responds to reframe question
  async function handleFirstSubmit() {
    if (!userResponse.trim() || uiState === 'reflecting') return
    // Same words as the reply already on hand (the person went back and came
    // forward again): show it, and keep the card and the rest as they were.
    if (claudeReply && replyFor.current === userResponse.trim()) {
      setUiState('card-selection')
      return
    }
    // New words: the card, the second reply and the turnaround were all written
    // from the old ones.
    setCardOpened(null)
    setFinalReply(null)
    setTurnaroundReflection(null)
    setUiState('reflecting')

    const msgs: SessionMessage[] = [
      ...buildBaseMessages(),
      { role: 'user', content: userResponse.trim() },
    ]

    const result = await callClaude({ ...opts, messages: msgs })
    if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
    setClaudeReply(result.response)
    replyFor.current = userResponse.trim()
    // After Turn 1 → card selection moment
    setUiState('card-selection')
  }

  // ─── The Work — fetch turnaround reflection ──────────────────────────────
  // Triggered when entering 'turnaround-input' state. Sends [TW-TURNAROUND]
  // to the Edge Function, which returns 2-3 turnaround variants of the original
  // belief plus concrete examples — then asks "which feels most alive?".
  async function fetchTurnaround() {
    setTurnaroundLoading(true)
    const msgs: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: `[Pattern named: "${patternReflection}"]` },
      { role: 'user', content: `[Reframe response] ${userResponse.trim()}` },
      ...(deepeningResponse.trim() ? [{ role: 'user' as const, content: `[Deepening response] ${deepeningResponse.trim()}` }] : []),
      { role: 'user', content: '[TW-TURNAROUND]' },
    ]
    const result = await callClaude({ ...opts, messages: msgs })
    setTurnaroundLoading(false)
    if ('error' in result) {
      // Show the phase's usual "something went wrong, try again" screen. This used
      // to leave the reflection empty, and the screen shows a spinner for as long
      // as the reflection is empty: a failed call spun forever (#70). Try again
      // comes back here and asks for the turnaround again; Back goes to the last
      // screen that worked.
      setErrorCode(errorCodeOf(result))
      setErrorRetryTo('turnaround-input')
      setUiState('error')
    } else {
      setTurnaroundReflection(result.response)
    }
  }

  // ─── The Work — finalize after turnaround reflection ────────────────────
  // The turnaround response is logged but does NOT pollute reframeResponse.
  // The reframe field stays the original Turn 1 user response — clean data model.
  function handleTurnaroundComplete() {
    // Could persist turnaroundResponse to a future analytics table; for now
    // it's a contemplative moment that enriches the live session only.
    onComplete(userResponse.trim())
  }

  // Turn 2: user responds to Claude's deepening question (optional)
  async function handleDeepeningSubmit() {
    if (!deepeningResponse.trim() || uiState === 'deepening-reflecting') return
    if (finalReply && finalFor.current === deepeningResponse.trim()) {
      setUiState('done')
      return
    }
    setTurnaroundReflection(null)
    setUiState('deepening-reflecting')

    const msgs: SessionMessage[] = [
      ...buildBaseMessages(),
      { role: 'user', content: userResponse.trim() },
      { role: 'assistant', content: claudeReply! },
      { role: 'user', content: deepeningResponse.trim() },
    ]

    const result = await callClaude({ ...opts, messages: msgs })
    if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
    setFinalReply(result.response)
    finalFor.current = deepeningResponse.trim()
    setUiState('done')
  }

  /** The screen for the current state. The insight card is drawn separately, below. */
  function renderScreen(): ReactNode {
    if (uiState === 'error') {
      return (
        <div className="flex-1 flex flex-col items-center justify-center px-6 page-enter">
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>{t('errors.somethingWrong')}</p>
          {errorCode && (
            <p className="text-xs mb-4 text-center max-w-xs" style={{ color: '#6B5F7A' }}>{t(guideErrorKey(errorCode))}</p>
          )}
          <button
            onClick={() => { setErrorCode(null); setUiState(errorRetryTo); setErrorRetryTo('generating') }}
            style={{ background: '#3D1F62', color: 'white', border: 'none', borderRadius: '9999px', padding: '0.75rem 1.5rem', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            {t('errors.tryAgain')}
          </button>
        </div>
      )
    }

    // The insight card is shown in place of Shift's own UI. It is drawn by the
    // slot at the end of this component, not here, so it can stay alive after the
    // person moves on from it.
    if (uiState === 'showing-card') return null

    if (uiState === 'intuitive-shift') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
            <IntuitiveChoice
              initialState={feelingPick}
              onStateChange={setFeelingPick}
              prompt={t('shift.reframeQuestion')}
              options={shiftOptions}
              isLoadingOptions={optionsLoading}
              placeholder={t('shift.orDescribe')}
              ctaLabel={t('session.continue')}
              skipLabel={t('shift.skipContinue')}
              onComplete={value => {
                // The same feeling after going back: the reframe question already
                // written for it is still the right one.
                if (reframeQuestion && value === shiftFeeling) {
                  setUiState('awaiting')
                  return
                }
                // A different feeling: the question, and everything written after
                // it, was written from the old one.
                setShiftFeeling(value)
                setReframeQuestion(null)
                setClaudeReply(null)
                setFinalReply(null)
                setCardOpened(null)
                setTurnaroundReflection(null)
                replyFor.current = null
                finalFor.current = null
                setUiState('generating')
              }}
            />
          </div>
        </div>
      )
    }

    if (uiState === 'generating') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="px-6 py-10 max-w-xl mx-auto w-full flex flex-col flex-1">
            <div className="mt-4">
              <LoadingSpiral size={22} label={t('shift.reflectingOnSeen')} />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="px-6 py-10 max-w-xl mx-auto w-full" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>

          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>{t('session.phases.shift')}</p>
          </div>

          {/* Reframe question — shown only while user is composing their first response */}
          {(uiState === 'awaiting' || uiState === 'reflecting') && (
            <div className="mb-8 message-appear">
              <p className="font-display leading-relaxed whitespace-pre-line"
                style={{ color: '#1C1128', fontSize: '1.2rem', fontStyle: 'italic', lineHeight: 1.5 }}>
                {reframeQuestion}
              </p>
            </div>
          )}

          {/* Turn 1 — awaiting first response */}
          {uiState === 'awaiting' && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {/* Response textarea — wrapped in Shift amber card (#B87D0A) at 4% tint */}
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(184,125,10,0.04)',
                borderTop: '3px solid #B87D0A',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#B87D0A' }}>
                  {t('shift.yourResponse')}
                </p>
                <VoiceButton
                  onTranscript={t => setUserResponse(prev => prev ? `${prev} ${t}` : t)}
                  accentColor="#B87D0A"
                >
                  <textarea
                    value={userResponse}
                    onChange={e => setUserResponse(e.target.value)}
                    placeholder={t('shift.takeYourTime')}
                    rows={5}
                    maxLength={2000}
                    autoFocus
                    className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                    style={{ borderColor: '#B87D0A', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                    onFocus={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = '0 0 0 3px rgba(184,125,10,0.18)' }}
                    onBlur={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = 'none' }}
                  />
                </VoiceButton>
                <p className="text-xs mt-2" style={{ color: '#C4B5D4' }}>
                  {t('shift.noWrongAnswers')}
                </p>
              </div>
              <div style={{ flex: 1 }} />
              <button
                onClick={handleFirstSubmit}
                disabled={!userResponse.trim()}
                className="w-full py-4 rounded-xl font-medium tracking-wide mt-6"
                style={{
                  background: userResponse.trim() ? '#3D1F62' : '#E4D5EE',
                  color: userResponse.trim() ? 'white' : '#9B8FAA',
                  border: 'none',
                  cursor: userResponse.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('session.continue')}
              </button>
            </div>
          )}

          {/* Loading spiral — turn 1 */}
          {uiState === 'reflecting' && (
            <LoadingSpiral size={22} label={t('session.generating')} />
          )}

          {/* Card selection — choose Soul Perspective or Reframe Invitation */}
          {uiState === 'card-selection' && claudeReply && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} className="message-appear">

              {/* User response echo — card-selection state */}
              <div
                className="mb-5 px-4 py-3 rounded-xl"
                style={{
                  // Amber-tinted background — Shift phase accent at ~4% opacity
                  background: 'rgba(184,125,10,0.04)',
                  // Left-border accent recipe for small inline context cards
                  borderTop: '1.5px solid #E4D5EE',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '3px solid #B87D0A',
                }}
              >
                <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('shift.youWrote')}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>{userResponse}</p>
              </div>

              <p className="text-base leading-relaxed font-display italic mb-8" style={{ color: '#1C1128' }}>
                {claudeReply}
              </p>

              <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                {t('shift.insightCards')}
              </p>
              <p className="text-sm mb-6" style={{ color: '#6B5F7A' }}>
                {t('shift.chooseInsight')}
              </p>

              <div className="flex flex-col gap-3 mb-4">
                <button
                  onClick={() => { setCardOpened('soul_perspective'); setUiState('showing-card') }}
                  className="w-full px-5 py-4 rounded-xl text-left transition-all"
                  style={{ background: '#0F2040', border: '1.5px solid rgba(100,160,255,0.25)', cursor: 'pointer' }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: 'rgba(100,160,255,0.7)' }}>
                    {t('shift.soulPerspective')}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {t('shift.soulPerspectiveDesc')}
                  </p>
                </button>

                <button
                  onClick={() => { setCardOpened('reframe_invitation'); setUiState('showing-card') }}
                  className="w-full px-5 py-4 rounded-xl text-left transition-all"
                  style={{ background: '#0F2A1A', border: '1.5px solid rgba(100,200,140,0.25)', cursor: 'pointer' }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: 'rgba(100,200,140,0.7)' }}>
                    {t('shift.reframeInvitation')}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {t('shift.reframeInvitationDesc')}
                  </p>
                </button>
              </div>

              <button
                onClick={() => setUiState('deepening-input')}
                className="w-full py-3 rounded-xl text-sm"
                style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
              >
                {t('shift.skipToNext')}
              </button>
            </div>
          )}

          {/* Turn 2 — compact summary of what was explored, then optional deepening input */}
          {(uiState === 'deepening-input' || uiState === 'deepening-reflecting') && claudeReply && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} className="message-appear">

              {/* Compact echo of user's first response — replaces repeating the full claudeReply */}
              <div
                className="mb-6 px-4 py-3 rounded-xl"
                style={{
                  background: 'rgba(184,125,10,0.04)',
                  borderTop: '1.5px solid #E4D5EE',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '3px solid #B87D0A',
                }}
              >
                <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('shift.youExplored')}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>{userResponse}</p>
              </div>

              {/* The guide's reflection + deepening question — this is what the
                  "Your response" box below is answering. Without it the box has no
                  prompt (the question lives in claudeReply, e.g. "What do you know
                  about yourself now that you didn't before?"). */}
              <p className="text-base leading-relaxed font-display italic mb-6" style={{ color: '#1C1128' }}>
                {claudeReply}
              </p>

              {uiState === 'deepening-input' && (
                <>
                  {/* Deepening response textarea — wrapped in Shift amber card (#B87D0A) at 4% tint */}
                  <div className="rounded-2xl px-5 py-5" style={{
                    background: 'rgba(184,125,10,0.04)',
                    borderTop: '3px solid #B87D0A',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}>
                    <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#B87D0A' }}>
                      {t('shift.yourResponse')}
                    </p>
                    <VoiceButton
                      onTranscript={t => { setDeepeningResponse(prev => prev ? `${prev} ${t}` : t); setShowGuided(false) }}
                      accentColor="#B87D0A"
                    >
                      <textarea
                        value={deepeningResponse}
                        onChange={e => { setDeepeningResponse(e.target.value); setShowGuided(false) }}
                        placeholder={t('shift.takeYourTimeOrContinue')}
                        rows={4}
                        maxLength={2000}
                        autoFocus
                        className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                        style={{ borderColor: '#B87D0A', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                        onFocus={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = '0 0 0 3px rgba(184,125,10,0.18)' }}
                        onBlur={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = 'none' }}
                      />
                    </VoiceButton>

                    {!deepeningResponse.trim() && !showGuided && (
                      <button
                        onClick={() => setShowGuided(true)}
                        className="text-sm mt-2 self-start"
                        style={{ color: '#9B72C0', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {t('shift.notSureHow')}
                      </button>
                    )}
                  </div>

                  {showGuided && !deepeningResponse.trim() && (
                    <div
                      className="mt-3 px-4 py-3 rounded-xl message-appear"
                      style={{
                        // Amber-tinted background — Shift phase accent at ~4% opacity
                        background: 'rgba(184,125,10,0.04)',
                        // Left-border accent recipe for small inline context cards
                        borderTop: '1.5px solid #E4D5EE',
                        borderRight: '1.5px solid #E4D5EE',
                        borderBottom: '1.5px solid #E4D5EE',
                        borderLeft: '3px solid #B87D0A',
                      }}
                    >
                      <p className="text-sm leading-relaxed mb-3" style={{ color: '#6B5F7A' }}>
                        {t('shift.thatsOkayTry')}
                      </p>
                      {guidedPrompts.map(prompt => (
                        <button
                          key={prompt}
                          onClick={() => { setDeepeningResponse(prompt); setShowGuided(false) }}
                          className="block w-full text-left px-4 py-3 rounded-lg mb-2 transition-all"
                          style={{ background: 'white', border: '1.5px solid #D4C0E8', color: '#3D1F62', cursor: 'pointer', fontSize: '0.9375rem', lineHeight: 1.5 }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#B87D0A'; e.currentTarget.style.background = 'rgba(184,125,10,0.04)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#D4C0E8'; e.currentTarget.style.background = 'white' }}
                        >
                          "{prompt}"
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ flex: 1 }} />
                  <div className="flex flex-col gap-3 mt-6">
                    {deepeningResponse.trim() && (
                      <button
                        onClick={handleDeepeningSubmit}
                        className="w-full py-4 rounded-xl font-medium tracking-wide"
                        style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                      >
                        {t('session.continue')}
                      </button>
                    )}
                    <button
                      // For The Work: even on skip, the turnaround is the closing
                      // ritual — preserve it. Standard mode: complete immediately.
                      onClick={() => sessionMode === 'the_work' ? setUiState('turnaround-input') : onComplete(userResponse.trim())}
                      className="w-full py-3 rounded-xl text-sm"
                      style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                    >
                      {deepeningResponse.trim() ? t('shift.skipReadyToMoveOn') : t('shift.readyToMoveOn')}
                    </button>
                  </div>
                </>
              )}

              {uiState === 'deepening-reflecting' && (
                <div className="mt-2">
                  <LoadingSpiral size={22} label={t('session.generating')} />
                </div>
              )}
            </div>
          )}

          {/* Turn 2 — final Claude reply */}
          {uiState === 'done' && finalReply && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} className="message-appear">

              {/* User response echo — done state (final reply) */}
              <div
                className="mb-5 px-4 py-3 rounded-xl"
                style={{
                  // Amber-tinted background — Shift phase accent at ~4% opacity
                  background: 'rgba(184,125,10,0.04)',
                  // Left-border accent recipe for small inline context cards
                  borderTop: '1.5px solid #E4D5EE',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '3px solid #B87D0A',
                }}
              >
                <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('shift.youWrote')}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>{deepeningResponse}</p>
              </div>

              <p className="text-base leading-relaxed font-display italic mb-8" style={{ color: '#1C1128' }}>
                {finalReply}
              </p>

              <div style={{ flex: 1 }} />

              <button
                // For The Work: route into the turnaround step instead of completing.
                // Standard mode preserves prior behaviour: completes Shift directly.
                onClick={() => sessionMode === 'the_work' ? setUiState('turnaround-input') : onComplete(userResponse.trim())}
                className="w-full py-4 rounded-xl font-medium tracking-wide"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </div>
          )}

          {/* ── The Work — Turnaround (final step) ─────────────────────────── */}
          {/* Triggered after Turn 2 'done' state when sessionMode === 'the_work'. */}
          {/* Claude generates 2-3 turnaround variants; user picks one + writes why. */}
          {uiState === 'turnaround-input' && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} className="message-appear">

              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#B87D0A' }}>
                {t('shift.theTurnaround')}
              </p>

              {/* Loading spinner while fetching the turnaround reflection */}
              {turnaroundLoading || !turnaroundReflection ? (
                <div className="mt-2">
                  <LoadingSpiral size={22} label={t('shift.findingTurnaround')} />
                </div>
              ) : (
                <>
                  {/* Claude's turnaround reflection — Shift amber card */}
                  <div className="mb-6 px-5 py-5 rounded-2xl" style={{
                    background: 'rgba(184,125,10,0.04)',
                    borderTop: '3px solid #B87D0A',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}>
                    <p className="text-base leading-relaxed font-display italic whitespace-pre-line" style={{ color: '#1C1128' }}>
                      {turnaroundReflection}
                    </p>
                  </div>

                  {/* User reflection on which turnaround feels alive */}
                  <div className="rounded-2xl px-5 py-5" style={{
                    background: 'rgba(184,125,10,0.04)',
                    borderTop: '3px solid #B87D0A',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}>
                    <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#B87D0A' }}>
                      {t('shift.whichFeelsTrue')}
                    </p>
                    <VoiceButton
                      onTranscript={t => setTurnaroundResponse(prev => prev ? `${prev} ${t}` : t)}
                      accentColor="#B87D0A"
                    >
                      <textarea
                        value={turnaroundResponse}
                        onChange={e => setTurnaroundResponse(e.target.value)}
                        placeholder={t('shift.takeYourTime')}
                        rows={4}
                        maxLength={2000}
                        autoFocus
                        className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                        style={{ borderColor: '#B87D0A', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                        onFocus={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = '0 0 0 3px rgba(184,125,10,0.18)' }}
                        onBlur={e => { e.target.style.borderColor = '#B87D0A'; e.target.style.boxShadow = 'none' }}
                      />
                    </VoiceButton>
                  </div>

                  <div style={{ flex: 1 }} />

                  <button
                    onClick={handleTurnaroundComplete}
                    className="w-full py-4 rounded-xl font-medium tracking-wide mt-6"
                    style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                  >
                    {t('session.continue')}
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    )
  }

  return (
    <>
      {renderScreen()}

      {/* The card the person opened. Kept alive once opened so that Back from the
          screens after it lands on the card's last screen as it was left. Keyed
          by type: a different card is a new one. */}
      {cardOpened && (
        <Retained key={cardOpened} active={uiState === 'showing-card'}>
          <InsightCard
            cardType={cardOpened}
            sessionId={sessionId}
            userId={userId}
            emotion={emotion}
            intensity={intensity}
            firstName={firstName}
            storyText={storyText}
            patternReflection={patternReflection}
            reframeResponse={userResponse.trim()}
            seriesContext={seriesContext}
            onComplete={() => setUiState('deepening-input')}
          />
        </Retained>
      )}
    </>
  )
}
